import { useCallback, useSyncExternalStore } from 'react'
import { useColleagues, useMe } from '@/shared/api/session'
import { BRANDS, loadBrand, persistBrand, type Brand, type BrandKey } from './branding'
import { maskPerson } from './anonymise'

/**
 * Module-level rather than per-component, for the same reason as the write-access guard:
 * the topbar, the loading skeleton and the handover card all read this, and with
 * useState they would be independent copies — flipping the switch would re-skin
 * one of them.
 */
let current: BrandKey = loadBrand()
const listeners = new Set<() => void>()

function subscribe(fn: () => void): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

function getSnapshot(): BrandKey {
  return current
}

/**
 * The brand on screen, without the roster useBrand also loads. For the logo,
 * which renders on the boot screen before anyone is signed in, where asking
 * for /api/users would only earn a 401.
 */
export function useActiveBrand(): Brand {
  const { data: me } = useMe()
  const key = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
  // Non-admins always see YLLW, even with the flag set. A stored value is
  // per-browser, so a shared or handed-down machine could otherwise leave a BDR
  // looking at a re-skinned product with no way to know why.
  //
  // Until /api/me answers, only the boot screen is up and nobody's role is
  // known yet. It follows the stored choice, which only an admin can have made
  // in this browser: opening a Solvigo demo on the YLLW logo would name the
  // customer before the app has even loaded.
  return !me || me.isAdmin ? BRANDS[key] : BRANDS.yllw
}

export function useBrand(): {
  brand: Brand
  /** True when this viewer is allowed to switch it at all. */
  canRebrand: boolean
  setBrand: (key: BrandKey) => void
  /** True while the customer's identity is being kept off the screen. */
  anonymised: boolean
  /** Every known internal address, for callers holding only a local-part. */
  roster: readonly string[]
  /**
   * A member of the internal team, masked while white-label is on. Pass the
   * email; a display name is accepted too and masked the same way, since a name
   * identifies the customer just as well as an address does.
   */
  person: (value?: string | null) => string
} {
  const { data: me } = useMe()
  // Cached hard (10-minute staleTime) and already fetched for the seller
  // pickers, so this is a cache read rather than a request per caller.
  const { data: colleagues } = useColleagues()

  const setBrand = useCallback((next: BrandKey) => {
    if (current === next) return
    current = next
    persistBrand(next)
    listeners.forEach(fn => fn())
  }, [])

  const isAdmin = !!me?.isAdmin
  const brand = useActiveBrand()
  const anonymised = brand.key !== 'yllw'
  const roster = colleagues?.users.map(u => u.email) ?? []
  return {
    brand,
    canRebrand: isAdmin,
    setBrand,
    anonymised,
    roster,
    person: (value?: string | null) =>
      anonymised
        ? maskPerson(value, roster)
        : (value || '—'),
  }
}
